import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DocumentDuplicateIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { adminApi } from '../services/api';

interface Template {
  id: number;
  name: string;
  title: string;
  body: string;
  type: string;
  category: string;
  created_at: string;
  usage_count: number;
}

export default function Templates() {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => adminApi.getTemplates(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminApi.deleteTemplate?.(id),
    onSuccess: () => {
      toast.success('Template deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: () => {
      toast.error('Failed to delete template');
    },
  });

  const templateList = templates?.data || [];

  const templateCategories = [
    { name: 'Welcome', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    { name: 'Quiz', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
    { name: 'News', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
    { name: 'Achievement', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
    { name: 'Alert', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  ];

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Templates</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage reusable notification templates
          </p>
        </div>
        <button className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow">
          <PlusIcon className="w-5 h-5" />
          Create Template
        </button>
      </div>

      {/* Template categories */}
      <div className="flex gap-2 flex-wrap">
        <button className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-medium">
          All Templates
        </button>
        {templateCategories.map((category) => (
          <button
            key={category.name}
            className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Templates grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="inline-flex items-center justify-center w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Sample templates */}
          {[
            {
              id: 1,
              name: 'Welcome Message',
              title: 'Welcome to Clarity!',
              body: 'Start your UPSC preparation journey with us.',
              type: 'standard',
              category: 'Welcome',
              created_at: '2024-01-15',
              usage_count: 45,
            },
            {
              id: 2,
              name: 'Daily Quiz',
              title: 'Today\'s Quiz is Ready!',
              body: 'Test your knowledge with today\'s quiz questions.',
              type: 'quiz',
              category: 'Quiz',
              created_at: '2024-01-10',
              usage_count: 120,
            },
            {
              id: 3,
              name: 'Achievement Unlocked',
              title: '🏆 Achievement Unlocked!',
              body: 'Congratulations on completing your streak!',
              type: 'achievement',
              category: 'Achievement',
              created_at: '2024-01-08',
              usage_count: 67,
            },
          ].map((template) => (
            <div
              key={template.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Template preview */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                      <DocumentDuplicateIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {template.name}
                      </h3>
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                        templateCategories.find(c => c.name === template.category)?.color || ''
                      }`}>
                        {template.category}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {template.title}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                    {template.body}
                  </p>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>Used {template.usage_count} times</span>
                  <span>{new Date(template.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
                <button
                  onClick={() => {
                    setSelectedTemplate(template);
                    setShowPreview(true);
                  }}
                  className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                  title="Preview"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
                <button className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 transition-colors" title="Edit">
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteMutation.mutate(template.id)}
                  className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                  title="Delete"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Template Preview</h3>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
              <p className="font-medium">{selectedTemplate.title}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {selectedTemplate.body}
              </p>
            </div>
            <button
              onClick={() => setShowPreview(false)}
              className="mt-4 w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}